// Phase I0.5 — DailyFixtureRail.
//
// Shared horizontal-scrolling fixture rail for Daily Acca and Super Acca
// surfaces. Reads real fixtures from `useTodayFixtures` (next 24 hours
// of active rows). Groups by league. Renders status pills for
// UPCOMING / LIVE / SETTLED. Component is mounted on no page in this
// phase — it is groundwork for I3 / I4.
//
// Faithful to prototype/daily-fixture-rail.jsx structure but split into
// FixtureCard + FixtureStatusPill so this file stays focused on the
// scroller, header, grouping, and empty/loading/error variants.

import { useMemo, useRef, useState, type CSSProperties } from "react";
import { Eyebrow } from "./Eyebrow";
import { FixtureCard } from "./FixtureCard";
import { useTodayFixtures, type TodayFixture } from "../../super-acca/useTodayFixtures";

export interface DailyFixtureRailProps {
  /** Optional callback when a fixture card is selected. */
  onSelect?: (fixtureId: string) => void;
  /** Optional initial selection. */
  selectedId?: string | null;
  style?: CSSProperties;
}

const arrowBtn: CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 3,
  background: "transparent",
  color: "var(--sa-fg-primary)",
  border: "1px solid var(--sa-hairline)",
  fontFamily: "var(--sa-font-mono)",
  fontSize: 16,
  lineHeight: 1,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const statusMessageStyle: CSSProperties = {
  padding: "20px 32px",
  fontFamily: "var(--sa-font-mono)",
  fontSize: 11,
  letterSpacing: "0.16em",
};

interface RailHeaderProps {
  count: number;
  liveCount: number;
  upcomingCount: number;
  onScrollLeft: () => void;
  onScrollRight: () => void;
}

function RailHeader({ count, liveCount, upcomingCount, onScrollLeft, onScrollRight }: RailHeaderProps) {
  return (
    <div
      style={{
        padding: "20px 32px 14px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
      }}
    >
      <div>
        <Eyebrow>Today's Fixtures</Eyebrow>
        <div
          style={{
            fontFamily: "var(--sa-font-display)",
            fontSize: 26,
            fontWeight: 600,
            marginTop: 6,
            color: "var(--sa-fg-primary)",
          }}
        >
          What's coming up{" "}
          <span style={{ color: "var(--sa-accent-saffron)" }}>·</span> {count}{" "}
          {count === 1 ? "match" : "matches"}
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 10.5,
            color: "var(--sa-fg-muted)",
            letterSpacing: "0.16em",
            marginRight: 8,
          }}
        >
          ● {liveCount} LIVE · {upcomingCount} UPCOMING
        </span>
        <button type="button" onClick={onScrollLeft} style={arrowBtn} aria-label="Scroll fixtures left">
          ‹
        </button>
        <button type="button" onClick={onScrollRight} style={arrowBtn} aria-label="Scroll fixtures right">
          ›
        </button>
      </div>
    </div>
  );
}

function groupByLeague(fixtures: readonly TodayFixture[]): Array<[string, TodayFixture[]]> {
  const out = new Map<string, TodayFixture[]>();
  for (const f of fixtures) {
    const arr = out.get(f.league) ?? [];
    arr.push(f);
    out.set(f.league, arr);
  }
  return Array.from(out.entries());
}

export function DailyFixtureRail({ onSelect, selectedId = null, style }: DailyFixtureRailProps) {
  const { fixtures, loading, error } = useTodayFixtures();
  const [internalSelected, setInternalSelected] = useState<string | null>(selectedId);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const grouped = useMemo(() => groupByLeague(fixtures), [fixtures]);
  const liveCount = fixtures.filter((f) => f.status === "live").length;
  const upcomingCount = fixtures.filter((f) => f.status === "upcoming").length;

  const scroll = (dir: number) => {
    scrollRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  const pick = (id: string) => {
    setInternalSelected(id);
    onSelect?.(id);
  };

  return (
    <div
      className="sa-root"
      style={{
        borderBottom: "1px solid var(--sa-hairline)",
        background: "var(--sa-bg-base)",
        paddingBottom: 4,
        ...style,
      }}
    >
      <RailHeader
        count={fixtures.length}
        liveCount={liveCount}
        upcomingCount={upcomingCount}
        onScrollLeft={() => scroll(-1)}
        onScrollRight={() => scroll(1)}
      />
      {loading ? (
        <div style={{ ...statusMessageStyle, color: "var(--sa-fg-muted)" }}>LOADING FIXTURES…</div>
      ) : error ? (
        <div style={{ ...statusMessageStyle, color: "var(--sa-signal-red)" }}>ERR · {error}</div>
      ) : fixtures.length === 0 ? (
        <div style={{ ...statusMessageStyle, color: "var(--sa-fg-muted)" }}>
          NO FIXTURES IN THE NEXT 24 HOURS.
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="sa-fixture-rail-scroll"
          style={{
            display: "flex",
            gap: 24,
            overflowX: "auto",
            overflowY: "hidden",
            padding: "4px 32px 22px",
            scrollSnapType: "x proximity",
            scrollbarWidth: "thin",
          }}
        >
          {grouped.map(([league, list]) => (
            <div
              key={league}
              style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 280 }}
            >
              <span
                style={{
                  fontFamily: "var(--sa-font-mono)",
                  fontSize: 9,
                  color: "var(--sa-fg-muted)",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                }}
              >
                ▲ {league}
              </span>
              <div style={{ display: "flex", gap: 12 }}>
                {list.map((f) => (
                  <FixtureCard
                    key={f.id}
                    fix={f}
                    active={internalSelected === f.id}
                    onClick={() => pick(f.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
